import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to detect iOS keyboard height using Visual Viewport API.
 * Returns the keyboard height in pixels (0 when keyboard is hidden).
 * 
 * Uses Visual Viewport API which is the only reliable way to detect
 * keyboard state on iOS Safari - focus/blur events are unreliable,
 * and dvh doesn't update when keyboard opens.
 */
export function useKeyboardHeight() {
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  const updateKeyboardHeight = useCallback(() => {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    const viewport = window.visualViewport;
    // Calculate keyboard height as difference between window height and visual viewport height
    // Account for any scroll offset
    const keyboardH = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
    
    setKeyboardHeight(keyboardH);
    setIsKeyboardVisible(keyboardH > 100); // Threshold to avoid false positives from toolbar
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    const viewport = window.visualViewport;
    
    // Initial check
    updateKeyboardHeight();

    // Listen for viewport changes
    viewport.addEventListener('resize', updateKeyboardHeight);
    viewport.addEventListener('scroll', updateKeyboardHeight);

    return () => {
      viewport.removeEventListener('resize', updateKeyboardHeight);
      viewport.removeEventListener('scroll', updateKeyboardHeight);
    };
  }, [updateKeyboardHeight]);

  return { keyboardHeight, isKeyboardVisible };
}

/**
 * Scroll an element into view when keyboard opens.
 * Use with onFocus on input elements.
 */
export function scrollInputIntoView(element: HTMLElement | null) {
  if (!element) return;
  
  // Small delay to let keyboard animation start
  setTimeout(() => {
    element.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center',
      inline: 'nearest'
    });
  }, 100);
}
